import React, { useState, useEffect, useRef } from 'react';

const THEMES = [
  { id: 'light-blue', name: 'Light Blue', icon: '🔵' },
  { id: 'light-purple', name: 'Light Purple', icon: '🟣' },
  { id: 'light-green', name: 'Light Green', icon: '🟢' },
  { id: 'light-orange', name: 'Light Orange', icon: '🟠' },
  { id: 'light-pink', name: 'Light Pink', icon: '🩷' },
  { id: 'light-teal', name: 'Light Teal', icon: '🔷' },
  { id: 'light-red', name: 'Light Red', icon: '🔴' },
  { id: 'light-amber', name: 'Light Amber', icon: '🟡' },
  { id: 'light-indigo', name: 'Light Indigo', icon: '🟣' },
  { id: 'light-cyan', name: 'Light Cyan', icon: '💠' },
  { id: 'light-lime', name: 'Light Lime', icon: '🟢' },
  { id: 'light-rose', name: 'Light Rose', icon: '🌹' }
];

const ThemePicker = ({ onThemeChange, showToast }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('light-blue');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('dashboard-theme') || 'light-blue';
    setCurrentTheme(savedTheme);
    applyTheme(savedTheme);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const applyTheme = (themeId) => {
    // Apply to documentElement
    document.documentElement.setAttribute('data-theme', themeId);
    
    // Also apply to body for immediate effect
    document.body.setAttribute('data-theme', themeId);
    
    // Force a style recalculation
    const currentDisplay = document.body.style.display;
    document.body.style.display = 'none';
    void document.body.offsetHeight; // Trigger reflow
    document.body.style.display = currentDisplay;
    
    console.log('✅ Theme applied:', themeId);
    console.log('Current data-theme:', document.documentElement.getAttribute('data-theme'));
  };

  const handleThemeChange = (themeId) => {
    console.log('🎨 Changing theme to:', themeId);
    
    setCurrentTheme(themeId);
    applyTheme(themeId);
    localStorage.setItem('dashboard-theme', themeId);
    setIsOpen(false);

    const theme = THEMES.find(t => t.id === themeId);
    if (showToast) {
      showToast(`🎨 Theme changed to ${theme?.name}`, 'info', 2000);
    }
    
    if (onThemeChange) {
      onThemeChange(themeId);
    }
  };

  const currentThemeData = THEMES.find(t => t.id === currentTheme);

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button
        className="theme-picker-btn"
        onClick={() => {
          console.log('Theme picker clicked, current state:', isOpen);
          setIsOpen(!isOpen);
        }}
        title="Change Color Scheme"
        aria-label="Change Color Scheme"
        style={{
          width: '40px',
          height: '40px',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.25rem',
          color: '#1e293b',
          transition: 'all 0.3s ease',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
        }}
      >
        {currentThemeData?.icon || '🎨'}
      </button>

      {isOpen && (
        <div 
          className="theme-picker-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            width: '360px',
            maxHeight: '500px',
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '1rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            padding: '1.5rem',
            zIndex: 1000,
            animation: 'slideInDown 0.3s ease-out'
          }}
        >
          <div style={{ 
            paddingBottom: '1rem', 
            borderBottom: '1px solid #e5e7eb', 
            marginBottom: '1rem' 
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: 700, 
              color: '#1e293b',
              marginBottom: '0.5rem'
            }}>
              🎨 Choose Your Color Scheme
            </h3>
            <p style={{ 
              fontSize: '0.75rem', 
              color: '#64748b' 
            }}>
              Select an accent color - white background remains constant
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '0.75rem'
          }}>
            {THEMES.map(theme => (
              <button
                key={theme.id}
                className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
                onClick={() => handleThemeChange(theme.id)}
                data-theme={theme.id}
                style={{
                  padding: '0.75rem',
                  background: currentTheme === theme.id ? getThemeColor(theme.id, 'primary') : 'white',
                  border: `2px solid ${currentTheme === theme.id ? getThemeColor(theme.id, 'primary') : '#e5e7eb'}`,
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  if (currentTheme !== theme.id) {
                    e.currentTarget.style.borderColor = getThemeColor(theme.id, 'primary');
                    e.currentTarget.style.background = '#f9fafb';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentTheme !== theme.id) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.background = 'white';
                  }
                }}
              >
                <div style={{
                  display: 'flex',
                  gap: '3px',
                  height: '24px',
                  borderRadius: '0.375rem',
                  overflow: 'hidden'
                }}>
                  <div style={{ flex: 1, background: getThemeColor(theme.id, 'primary') }}></div>
                  <div style={{ flex: 1, background: getThemeColor(theme.id, 'secondary') }}></div>
                  <div style={{ flex: 1, background: getThemeColor(theme.id, 'accent') }}></div>
                </div>
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: currentTheme === theme.id ? 'white' : '#1e293b',
                  textAlign: 'center'
                }}>
                  {theme.icon} {theme.name.replace('Light ', '')}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get theme colors for preview
const getThemeColor = (themeId, colorType) => {
  const colors = {
    'light-blue': { primary: '#3b82f6', secondary: '#60a5fa', accent: '#93c5fd' },
    'light-purple': { primary: '#8b5cf6', secondary: '#a78bfa', accent: '#c4b5fd' },
    'light-green': { primary: '#10b981', secondary: '#34d399', accent: '#6ee7b7' },
    'light-orange': { primary: '#f97316', secondary: '#fb923c', accent: '#fdba74' },
    'light-pink': { primary: '#ec4899', secondary: '#f472b6', accent: '#f9a8d4' },
    'light-teal': { primary: '#14b8a6', secondary: '#2dd4bf', accent: '#5eead4' },
    'light-red': { primary: '#ef4444', secondary: '#f87171', accent: '#fca5a5' },
    'light-amber': { primary: '#f59e0b', secondary: '#fbbf24', accent: '#fcd34d' },
    'light-indigo': { primary: '#6366f1', secondary: '#818cf8', accent: '#a5b4fc' },
    'light-cyan': { primary: '#06b6d4', secondary: '#22d3ee', accent: '#67e8f9' },
    'light-lime': { primary: '#84cc16', secondary: '#a3e635', accent: '#bef264' },
    'light-rose': { primary: '#f43f5e', secondary: '#fb7185', accent: '#fda4af' }
  };
  
  return colors[themeId]?.[colorType] || '#3b82f6';
};

export default ThemePicker;